<template>
    <div>
        <div v-if="user && timezones.length > 0" class="row">
            <div class="col-12 col-md-8 order-2 order-md-1">
                <h4>{{ user.full_name }}<span class="small">（{{ user.full_name_kana }}）</span></h4>

                <template v-if="userType !== 'doctor'">
                    <div class="d-flex">
                        <div>{{ $locale.patient }}番号：</div>
                        <div>#{{ user.id }}</div>
                    </div>
                    <div class="d-flex">
                        <div>メールアドレス：</div>
                        <div>{{ user.email }}</div>
                    </div>
                    <div class="d-flex">
                        <div>電話番号：</div>
                        <div>{{ user.tel }}</div>
                    </div>
                    <div
                        v-if="user.country != '日本'"
                        class="d-flex">
                        <div class="d-flex justify-content-start">
                            <span>住所：</span>
                            <div>
                                <div v-if="user.postcode != null && user.postcode != ''">{{ '〒' + user.postcode + " "}}</div>
                                <div v-if="user.address != null && user.address != ''" style="white-space: pre-wrap;">{{ user.address }}</div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-else
                        class="d-flex">
                        <div>
                            <span>住所：</span>
                            <span v-if="user.postcode != null && user.postcode != ''">{{ '〒' + user.postcode + " "}}</span>
                            <span v-if="user.prefecture != null && user.prefecture != ''">{{ user.prefecture}}</span>
                            <span v-if="user.address != null && user.address != ''">{{ user.address }}</span>
                            <span v-if="user.building != null && user.building != ''">{{ user.building }}</span>
                        </div>
                    </div>
                </template>

                <div
                    v-if="user.is_company"
                    class="d-flex">
                    <div>法人名：</div>
                    <div>{{ user.groups[0].name }}</div>
                </div>
                <div class="d-flex">
                    <div>タイムゾーン：</div>
                    <div>{{ dispTimezone }}</div>
                </div>

                <div class="mt-2">
                    <div class="d-flex">
                        <div>生年月日：</div>
                        <div>{{ helper.birthdayForHuman( user.birthday) }}</div>
                    </div>
                    <div class="d-flex">
                        <div>年齢：</div>
                        <div>{{ user.age }}</div>
                    </div>
                    <div class="d-flex">
                        <div>性別：</div>
                        <div>{{ user.sex ? '女性' : '男性' }}</div>
                    </div>
                    <div class="d-flex">
                        <div>身長：</div>
                        <div>{{ user.height }}cm</div>
                    </div>
                    <div class="d-flex">
                        <div>体重：</div>
                        <div>{{ user.weight }}kg</div>
                    </div>
                    <div class="d-flex">
                        <div>血液型：</div>
                        <div v-if="user.blood != null">{{ user.blood }}型</div>
                        <div v-else>未登録</div>
                    </div>
                </div>

                <div>
                    <div class="font-weight-bold mt-2">自己紹介文</div>
                    <div >{{ user.introduction }}</div>
                </div>

                <section v-if="userType === 'doctor'" class="mt-3">
                    <h6 class="font-weight-bold">{{ $locale.patient }}メモ
                        <img v-if="!isEdit"
                             class="icon-pencil mb-2"
                             src="/images/common/icon_pencil.svg"
                             @click="editMemo"
                        >
                    </h6>
                    <div v-if="isEdit" class="small">
                        <textarea v-model="doctor_memo"
                                  class="form-control small"
                                  :class="{ 'is-invalid' : errors.doctor_memo }"
                                  rows="6"
                                  @blur="updateDoctorMemo"
                                  placeholder="メモを入力"
                                  id="doctor_memo_area">
                        </textarea>
                        <div v-if="errors.doctor_memo"
                             class="invalid-feedback d-block">
                            {{ errors.doctor_memo[0] }}
                        </div>
                    </div>
                    <div v-else class="small">
                        <textarea v-model="doctor_memo"
                                  class="form-control small"
                                  rows="6"
                                  disabled
                        >
                        </textarea>
                    </div>
                </section>
            </div>

            <div class="col-12 col-md-4 order-1 order-md-2 mb-3">
                <img
                    :src="user.presigned_url"
                    :alt="user.full_name"
                    class="u-fit">
            </div>
        </div>
        <div
            v-else-if="!user || user.deleted_at !== null"
            class="text-muted">
            退会済の{{ $locale.patient }}
        </div>
    </div>
</template>

<script>
    import constants from '../../constants';
    import YokumiruHelper from '../../YokumiruHelper';
    import moment from "moment-timezone";

    export default {
        props: {
            user: {
                type: Object,
                default: null,
            },
            doctorId: {
                type: Number,
                default: null,
            },
            userType: {
                type: String,
                required: true,
            },
        },

        data: function () {
            return {
                doctor_memo: null,
                isEdit: false,
                errors: null,
                helper: YokumiruHelper,
                timezones: [],
            }
        },

        computed: {
            dispTimezone: function () {
                if (!this.user) return ''
                const tz = this.timezones.find(timezone => {
                    return timezone.key === this.user.timezone
                })
                return tz ? tz.value : ''
            },
        },

        created: function() {
            this.fetchTimezones()

            if (this.userType === 'doctor') {
                this.fetchDoctorMemo()
            }
        },

        methods: {
            fetchTimezones: function() {
                let endpoint = ''
                if (this.userType === 'doctor') {
                    endpoint = constants.doctor.profile.endpoint.timezone
                }
                else if (this.userType === 'admin') {
                    endpoint = constants.admin.profile.endpoint.timezone
                }
                else {
                    endpoint = constants.user.profile.endpoint.timezone
                }

                axios.get(endpoint)
                    .then(({data}) => {
                        const momentTimezones = moment.tz.names()
                        this.timezones = data.data.filter((d) => momentTimezones.includes(d.key))
                    })
            },

            fetchDoctorMemo: function () {
                if (!this.user || !this.doctorId) {
                    return
                }

                axios.get(`${constants.doctor.doctor_memo.endpoint.base}/${this.doctorId}/${this.user.id}`)
                    .then((response) => {
                        this.errors = {};
                        this.doctor_memo = response.data.doctor_memo;
                    })
                    .catch(({response}) => {
                        if (response.data.errors) {
                            this.errors = response.data.errors;
                        }
                    });
            },

            editMemo: function () {
                if (!this.doctorId) {
                    return
                }

                this.isEdit = !this.isEdit;
                this.$nextTick(() => document.getElementById('doctor_memo_area').focus());
            },

            /**
             * 患者メモ更新
             */
            updateDoctorMemo: function () {
                if (!this.user || !this.doctorId) {
                    return
                }

                const data = {
                    doctor_memo: this.doctor_memo,
                }

                axios.put(`${constants.doctor.doctor_memo.endpoint.base}/${this.doctorId}/${this.user.id}`, data)
                    .then(() => {
                        this.errors = {}
                        this.isEdit = false
                    })
                    .catch(({response}) => {
                        if (response.data.errors) {
                            this.errors = response.data.errors
                        }
                    });
            },
        },
    }
</script>

<style scoped lang="scss">
    .card-body img {
        max-width: 240px;
    }

    .icon-pencil {
        position: relative;
        top: 2px;
        width: 14px;
        margin-left: 3px;
        cursor: pointer;
    }
</style>
